import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../../../globals";
import HttpManager from "../../../../services/HttpManager";
import useEsTelefono from "../../../../utils/esTelefono";
import { serverError } from "../../../../components/modals/payments/register/registerPaymentModal";
import "./surchargeRules.css";
import horarioBonito from "../../../../utils/horarioBonito";
import { horarioAmPm, fechaHoraAmPm } from "../../../../utils/fechaHoraAmPm";

/*
	Recargos (Finanzas, solo Super Admin) — Cobros unificado, Etapa 2 (docs/ESPEC_COBROS_UNIFICADO.md).
	El recargo automático por pago tarde se configura por plantel y, si hace falta, por curso o por horario: el nivel más
	específico gana campo por campo y lo vacío hereda. Corre a las 12:00 am del plantel, el día siguiente al vencimiento.
	Nace apagado: nada se cobra hasta que el plantel tenga fecha «desde» y esté encendido (o un horario encendido como piloto).
	Todo lo valida el servidor; esta pantalla solo ayuda a verlo.
*/

type Step = { desde_dia?: number | string; monto?: number | string; cada_dias?: number | string; dias_del_mes?: number[] | string; suma?: number | string; maximo?: number | string };
type Rule = {
	id: number; campusId: number; courseId: number | null; scheduleId: number | null; scopeKey: string; level: "plantel" | "curso" | "horario";
	active: boolean | null; steps: Step[] | null; stopsOnRetire: boolean | null; validFrom: string | null; notes: string | null; updatedAt: string | null;
};
type Draft = { active: string; ownSteps: boolean; fixed: { day: string; amount: string }[];
	repeat: { on: boolean; kind: "mes" | "dias"; monthDays: string; every: string; add: string; max: string; from: string };
	stopsOnRetire: string; validFrom: string; notes: string };

const http = HttpManager.getInstance();
const tri = (v: boolean | null | undefined) => v === null || v === undefined ? "" : v ? "1" : "0";
const fromTri = (v: string) => v === "" ? null : v === "1";
const fecha = (d: string | null | undefined) => d ? d.slice(8, 10) + "/" + d.slice(5, 7) + "/" + d.slice(0, 4) : "—";

const toDraft = (r: Rule | null, isCampus: boolean): Draft => {
	const steps = r?.steps || null;
	const esRepetido = (s: Step) => s.cada_dias !== undefined || s.dias_del_mes !== undefined;
	const fixed = (steps || []).filter((s) => !esRepetido(s)).map((s) => ({ day: String(s.desde_dia ?? ""), amount: String(s.monto ?? "") }));
	const rep = (steps || []).find(esRepetido);
	return {
		active: isCampus ? (r?.active ? "1" : "0") : tri(r?.active), ownSteps: isCampus || !!steps,
		fixed: fixed.length ? fixed : [{ day: "1", amount: "" }],
		repeat: { on: !!rep, kind: rep && rep.cada_dias !== undefined ? "dias" : "mes",
			monthDays: Array.isArray(rep?.dias_del_mes) ? (rep?.dias_del_mes as number[]).join(", ") : String(rep?.dias_del_mes ?? "1, 16"),
			every: String(rep?.cada_dias ?? "15"), add: String(rep?.suma ?? ""), max: String(rep?.maximo ?? ""), from: rep?.desde_dia !== undefined ? String(rep.desde_dia) : "" },
		stopsOnRetire: isCampus ? (r?.stopsOnRetire === false ? "0" : "1") : tri(r?.stopsOnRetire),
		validFrom: r?.validFrom || "", notes: r?.notes || ""
	};
};
const draftSteps = (d: Draft): Step[] => {
	const out: Step[] = d.fixed.filter((f) => f.day !== "" || f.amount !== "").map((f) => ({ desde_dia: Number(f.day), monto: Number(f.amount) }));
	if (d.repeat.on && d.repeat.kind === "mes") out.push({ dias_del_mes: d.repeat.monthDays, suma: Number(d.repeat.add), maximo: Number(d.repeat.max) });
	else if (d.repeat.on) out.push({ cada_dias: Number(d.repeat.every), suma: Number(d.repeat.add), maximo: Number(d.repeat.max), ...(d.repeat.from !== "" ? { desde_dia: Number(d.repeat.from) } : {}) });
	return out;
};

/** Escalones en una línea: "300 desde el día 1 · 400 desde el 11 · +40 el 1 y el 16 de cada mes hasta 560". */
export const stepsText = (steps: Step[] | null | undefined, sym: string): string => {
	if (!steps || !steps.length) return "—";
	return steps.map((s) => s.dias_del_mes !== undefined
		? `+${sym}${s.suma} el ${(Array.isArray(s.dias_del_mes) ? s.dias_del_mes : String(s.dias_del_mes).split(/[\s,;]+/)).join(" y el ")} de cada mes hasta ${sym}${s.maximo}`
		: s.cada_dias !== undefined
		? `+${sym}${s.suma} cada ${s.cada_dias} días hasta ${sym}${s.maximo}${s.desde_dia !== undefined ? ` (desde el día ${s.desde_dia})` : ""}`
		: `${sym}${s.monto} desde el día ${s.desde_dia}`).join(" · ");
};

const RuleEditor: React.FC<{ campusId: number; rule: Rule | null; level: "plantel" | "curso" | "horario"; courseId?: number | null; scheduleId?: number | null;
	sym: string; title: string; onSaved: () => void; onCancel?: () => void }> = ({ campusId, rule, level, courseId, scheduleId, sym, title, onSaved, onCancel }) => {
	const isCampus = level === "plantel";
	const [d, setD] = useState<Draft>(toDraft(rule, isCampus));
	const [preview, setPreview] = useState<{ day: number; amount: number; date?: string }[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	// Los avisos de los montos («tiene que ser mayor que 0») salen al salir del campo o al guardar, no antes de escribir (QA 2026-10-09).
	const [tocado, setTocado] = useState<boolean>(false);
	// La fecha se escribe y se ve como dd/mm/aaaa, igual que en el aviso de arriba (el selector del navegador la ponía al revés).
	const isoADmy = (v: string) => (/^\d{4}-\d{2}-\d{2}$/.test(v || "") ? v.slice(8, 10) + "/" + v.slice(5, 7) + "/" + v.slice(0, 4) : "");
	const [fechaTexto, setFechaTexto] = useState<string>(isoADmy(toDraft(rule, isCampus).validFrom));
	useEffect(() => { setD(toDraft(rule, isCampus)); setFechaTexto(isoADmy(toDraft(rule, isCampus).validFrom)); setTocado(false); }, [rule, isCampus]);
	const fechaIso = (t: string): string | null => {
		const m = t.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
		return m ? m[3] + "-" + m[2].padStart(2, "0") + "-" + m[1].padStart(2, "0") : null;
	};
	const leerFecha = () => {
		const t = fechaTexto.trim();
		if (t === "") { setD((x) => ({ ...x, validFrom: "" })); return; }
		const m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
		if (!m || +m[2] < 1 || +m[2] > 12 || +m[1] < 1 || +m[1] > 31) { setError("La fecha va como dd/mm/aaaa (por ejemplo 06/11/2026)."); return; }
		const iso = m[3] + "-" + m[2].padStart(2, "0") + "-" + m[1].padStart(2, "0");
		setError(null);
		setFechaTexto(isoADmy(iso));
		setD((x) => ({ ...x, validFrom: iso }));
	};

	useEffect(() => {
		if (!d.ownSteps) { setPreview(null); return; }
		const t = setTimeout(() => {
			http.paymentService.previewSurchargeSteps(campusId, draftSteps(d)).then((r: any) => { setPreview(r.data.changes || []); setError(null); })
				.catch((e: any) => { setPreview(null); if (tocado) setError(serverError(e)); });
		}, 400);
		return () => clearTimeout(t);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(d.fixed), JSON.stringify(d.repeat), d.ownSteps, tocado]);

	const save = async () => {
		setTocado(true);
		try {
			setBusy(true); setError(null);
			await http.paymentService.saveSurchargeRule(campusId, {
				courseId: courseId || null, scheduleId: scheduleId || null,
				active: isCampus ? d.active === "1" : fromTri(d.active), steps: d.ownSteps ? draftSteps(d) : null,
				stopsOnRetire: isCampus ? d.stopsOnRetire === "1" : fromTri(d.stopsOnRetire),
				validFrom: isCampus ? (fechaIso(fechaTexto) ?? (d.validFrom || null)) : null, notes: d.notes
			});
			onSaved();
		} catch (e) {
			setError(serverError(e));
		} finally {
			setBusy(false);
		}
	};
	const remove = async () => {
		if (!rule || !window.confirm("¿Quitar esta regla? Vuelve a heredar lo de arriba.")) return;
		try { setBusy(true); await http.paymentService.deleteSurchargeRule(campusId, rule.id); onSaved(); }
		catch (e) { setError(serverError(e)); } finally { setBusy(false); }
	};
	const triSelect = (value: string, onChange: (v: string) => void, yes: string, no: string) => isCampus
		? <select className="form-select form-select-sm" value={value} onChange={(e) => onChange(e.target.value)}><option value="1">{yes}</option><option value="0">{no}</option></select>
		: <select className="form-select form-select-sm" value={value} onChange={(e) => onChange(e.target.value)}><option value="">Hereda</option><option value="1">{yes}</option><option value="0">{no}</option></select>;

	return (
		<div className="wrEditor">
			<div className="wrEditorTitulo">{title}</div>
			{error ? <div className="wrAviso">{error}</div> : null}
			<div className="wrGrid">
				<label className="wrCampo"><span>Recargo</span>{triSelect(d.active, (v) => setD({ ...d, active: v }), "Encendido", "Apagado")}</label>
				{isCampus ? (
					<label className="wrCampo"><span>Mensualidades que Vencen Desde</span>
						<input type="text" inputMode="numeric" className="form-control form-control-sm" placeholder="dd/mm/aaaa" value={fechaTexto}
							onChange={(e) => setFechaTexto(e.target.value)} onBlur={leerFecha} /></label>
				) : null}
				<label className="wrCampo"><span>Al Retirarse</span>{triSelect(d.stopsOnRetire, (v) => setD({ ...d, stopsOnRetire: v }), "Deja de Crecer", "Sigue Creciendo")}</label>
			</div>
			{!isCampus ? (
				<label className="wrCheck"><input type="checkbox" checked={d.ownSteps} onChange={(e) => setD({ ...d, ownSteps: e.target.checked })} /> Montos propios (si no, hereda los de arriba)</label>
			) : null}
			{d.ownSteps ? (
				<div className="wrEscalones" onBlurCapture={() => setTocado(true)}>
										{d.fixed.map((f, i) => (
						<div key={"f" + i} className="wrEscalon">
							<span>Desde el día</span>
							<input type="number" min="1" className="form-control form-control-sm wrNum" value={f.day} onChange={(e) => setD({ ...d, fixed: d.fixed.map((x, j) => j === i ? { ...x, day: e.target.value } : x) })} />
							<span>cobra</span>
							<input type="number" min="0" step="0.01" className="form-control form-control-sm wrMonto" value={f.amount} onChange={(e) => setD({ ...d, fixed: d.fixed.map((x, j) => j === i ? { ...x, amount: e.target.value } : x) })} />
							{d.fixed.length > 1 ? <button type="button" className="wrLink" onClick={() => setD({ ...d, fixed: d.fixed.filter((_, j) => j !== i) })}>Quitar</button> : null}
						</div>
					))}
					<button type="button" className="wrLink" onClick={() => setD({ ...d, fixed: [...d.fixed, { day: "", amount: "" }] })}>+ Escalón</button>
					<label className="wrCheck"><input type="checkbox" checked={d.repeat.on} onChange={(e) => setD({ ...d, repeat: { ...d.repeat, on: e.target.checked } })} /> Después sube solo</label>
					{d.repeat.on ? (
						<div className="wrEscalon">
							<select className="form-select form-select-sm wrTipo" value={d.repeat.kind} onChange={(e) => setD({ ...d, repeat: { ...d.repeat, kind: e.target.value as "mes" | "dias" } })}>
								<option value="mes">en días del mes</option>
								<option value="dias">cada tantos días</option>
							</select>
						</div>
					) : null}
					{d.repeat.on && d.repeat.kind === "mes" ? (
						<div className="wrEscalon">
							<span>el día</span>
							<input className="form-control form-control-sm wrDias" value={d.repeat.monthDays} placeholder="1, 16" onChange={(e) => setD({ ...d, repeat: { ...d.repeat, monthDays: e.target.value } })} />
							<span>de cada mes suma</span>
							<input type="number" min="0" step="0.01" className="form-control form-control-sm wrMonto" value={d.repeat.add} onChange={(e) => setD({ ...d, repeat: { ...d.repeat, add: e.target.value } })} />
							<span>hasta</span>
							<input type="number" min="0" step="0.01" className="form-control form-control-sm wrMonto" value={d.repeat.max} onChange={(e) => setD({ ...d, repeat: { ...d.repeat, max: e.target.value } })} />
						</div>
					) : null}
					{d.repeat.on && d.repeat.kind === "dias" ? (
						<div className="wrEscalon">
							<span>cada</span>
							<input type="number" min="1" className="form-control form-control-sm wrNum" value={d.repeat.every} onChange={(e) => setD({ ...d, repeat: { ...d.repeat, every: e.target.value } })} />
							<span>días suma</span>
							<input type="number" min="0" step="0.01" className="form-control form-control-sm wrMonto" value={d.repeat.add} onChange={(e) => setD({ ...d, repeat: { ...d.repeat, add: e.target.value } })} />
							<span>hasta</span>
							<input type="number" min="0" step="0.01" className="form-control form-control-sm wrMonto" value={d.repeat.max} onChange={(e) => setD({ ...d, repeat: { ...d.repeat, max: e.target.value } })} />
							<span className="wrTenue">empieza el día</span>
							<input type="number" min="1" className="form-control form-control-sm wrNum" placeholder="auto" value={d.repeat.from} onChange={(e) => setD({ ...d, repeat: { ...d.repeat, from: e.target.value } })} />
						</div>
					) : null}
					{preview && preview.length ? (
						<div className="wrPreview">
							<span className="wrTenue">Si vence el 5:</span>
							{preview.map((p) => <span key={"p" + p.day} className="wrChip">{p.date ? fecha(p.date).slice(0, 5) : "día " + p.day}: <b>{sym}{p.amount.toLocaleString("es-MX")}</b></span>)}
						</div>
					) : null}
				</div>
			) : null}
			<label className="wrCampo wrNotas"><span>Nota</span><input className="form-control form-control-sm" value={d.notes} onChange={(e) => setD({ ...d, notes: e.target.value })} /></label>
			<div className="wrAcciones">
				{onCancel ? <button type="button" className="wrBoton" disabled={busy} onClick={onCancel}>Cancelar</button> : null}
				{rule && !isCampus ? <button type="button" className="wrBoton" disabled={busy} onClick={remove}>Quitar Regla</button> : null}
				<button type="button" className="wrBoton wrPrimario" disabled={busy} onClick={save}>{busy ? "…" : "Guardar"}</button>
			</div>
		</div>
	);
};

const SurchargeRulesController: React.FC = () => {
	const movil = useEsTelefono();
	const sessionCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);
	const legacyCampusKey: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const legacyCampusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const legacyCampus: any = legacyCampusKey && legacyCampusRef ? legacyCampusRef[legacyCampusKey] : null;
	const campusId: number | null = legacyCampus?.id ?? sessionCampus?.id ?? null;

	const [data, setData] = useState<any>(null);
	const [error, setError] = useState<string | null>(null);
	const [editing, setEditing] = useState<string | null>(null);   // "k<curso>" | "s<horario>"
	const [newCourse, setNewCourse] = useState<string>("");
	const [buscar, setBuscar] = useState("");
	const [soloDistintos, setSoloDistintos] = useState(false);

	const load = () => {
		if (!campusId) return;
		http.paymentService.surchargeRules(campusId).then((r: any) => { setData(r.data); setError(null); }).catch((e: any) => setError(serverError(e)));
	};
	useEffect(() => {
		setData(null);
		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId]);

	const sym = data?.currency?.symbol || "";
	const campusName = legacyCampus?.name || sessionCampus?.name || "";
	const courseRule = (id: number) => (data?.courseRules || []).find((r: Rule) => r.courseId === id) || null;
	const scheduleRule = (id: number) => (data?.scheduleRules || []).find((r: Rule) => r.scheduleId === id) || null;
	const courseName = (id: number | null) => (data?.courses || []).find((c: any) => c.id === id)?.name || ("Curso " + id);
	/** "c1" → plantel; "c1-k10" → el curso; "c1-s4920" → el horario (la clave técnica no le dice nada a Caja). */
	const alcance = (k: string): string => {
		const c = k.match(/-k(\d+)$/), h = k.match(/-s(\d+)$/);
		if (c) return "curso " + courseName(Number(c[1]));
		if (h) { const s = (data?.schedules || []).find((x: any) => x.id === Number(h[1])); return "horario " + (s ? s.courseName + " " + horarioAmPm(s.name, horarioBonito) : h[1]); }
		return "plantel";
	};
	const schedules = useMemo(() => {
		const q = buscar.trim().toLowerCase();
		return (data?.schedules || []).filter((s: any) => (!q || (s.name + " " + s.courseName + " " + (s.code || "")).toLowerCase().includes(q))
			&& (!soloDistintos || s.differsFromLegacy || scheduleRule(s.id)));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, buscar, soloDistintos]);
	const fromLegacy = async (s: any) => {
		if (!campusId || !s.legacy.valid) return;
		if (!window.confirm(`Crear una regla para este horario con lo que trae escrito: ${stepsText(s.legacy.steps, sym)}?`)) return;
		try { await http.paymentService.saveSurchargeRule(campusId, { scheduleId: s.id, steps: s.legacy.steps, notes: "Desde los recargos escritos en el horario" }); load(); }
		catch (e) { setError(serverError(e)); }
	};

	if (!campusId) return <div className="container-fluid waModulo waRecargos" style={{ padding: 24 }}>Elige un plantel.</div>;
	const cr: Rule | null = data?.campusRule || null;
	const encendido = !!cr?.active && !!cr?.validFrom;
	return (
		<div className="container-fluid waModulo waRecargos" style={{ padding: movil ? "10px 12px 24px 12px" : "10px 24px 32px 24px" }}>
			<div className="wrCabeza">
				<h4 className="wrTitulo">Recargos</h4>
				<span className="wrTenue">{campusName}{data ? ` · hoy ${fecha(data.today)} · corre a las 12:00 am` : ""}</span>
			</div>
			{error ? <div className="wrAviso">{error}</div> : null}
			{!data ? <div className="loader" /> : (
				<>
					<div className={"wrEstado " + (encendido ? "wrEstadoOn" : "")}>
						{encendido ? <>Encendido · cobra a las mensualidades que vencen desde el <b>{fecha(cr!.validFrom)}</b></>
							: cr?.validFrom ? <>Apagado en el plantel · desde {fecha(cr.validFrom)}</>
								: <>Apagado · sin fecha «desde»</>}
						<span className="wrStats">{data.stats.total} recargos · {data.stats.pending} pendientes · {data.stats.paid} pagados · {data.stats.waived} eximidos · por cobrar {sym}{Number(data.stats.toCollect).toLocaleString("es-MX")}</span>
					</div>

					<h5 className="wrSeccion">Plantel</h5>
					{cr ? <RuleEditor campusId={campusId} rule={cr} level="plantel" sym={sym} title={"Regla de " + campusName} onSaved={load} />
						: <div className="wrAviso">Este plantel no tiene regla de recargo.</div>}

					<h5 className="wrSeccion">Por Curso</h5>
					{(data.courseRules || []).map((r: Rule) => editing === "k" + r.courseId
						? <RuleEditor key={r.scopeKey} campusId={campusId} rule={r} level="curso" courseId={r.courseId} sym={sym} title={courseName(r.courseId)} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
						: (
							<div key={r.scopeKey} className="wrFila">
								<b>{courseName(r.courseId)}</b>
								<span>{r.active === null ? "" : r.active ? "Encendido · " : "Apagado · "}{r.steps ? stepsText(r.steps, sym) : "montos del plantel"}</span>
								<button type="button" className="wrLink" onClick={() => setEditing("k" + r.courseId)}>Cambiar</button>
							</div>
						))}
					{editing && editing.startsWith("knew") ? (
						<RuleEditor campusId={campusId} rule={null} level="curso" courseId={Number(editing.slice(4))} sym={sym} title={courseName(Number(editing.slice(4)))}
							onSaved={() => { setEditing(null); setNewCourse(""); load(); }} onCancel={() => setEditing(null)} />
					) : (
						<div className="wrAgregar">
							<select className="form-select form-select-sm" value={newCourse} onChange={(e) => setNewCourse(e.target.value)}>
								<option value="">Agregar Regla para un Curso…</option>
								{(data.courses || []).filter((c: any) => !courseRule(c.id)).map((c: any) => <option key={"c" + c.id} value={c.id}>{c.name} ({c.schedules} horarios)</option>)}
							</select>
							<button type="button" className="wrBoton" disabled={!newCourse} onClick={() => setEditing("knew" + newCourse)}>Agregar</button>
						</div>
					)}

					<h5 className="wrSeccion">Por Horario</h5>
					<div className="wrFiltros">
						<input className="form-control form-control-sm" placeholder="Buscar horario o curso" value={buscar} onChange={(e) => setBuscar(e.target.value)} />
						<label className="wrCheck"><input type="checkbox" checked={soloDistintos} onChange={(e) => setSoloDistintos(e.target.checked)} /> Solo los que traen otros montos o tienen regla</label>
					</div>
					<div className="wrTabla">
						{schedules.map((s: any) => {
							const r = scheduleRule(s.id);
							if (editing === "s" + s.id) {
								return <RuleEditor key={"e" + s.id} campusId={campusId} rule={r} level="horario" scheduleId={s.id} sym={sym} title={s.courseName + " · " + horarioAmPm(s.name, horarioBonito)}
									onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />;
							}
							const eff = s.effective;
							return (
								<div key={"s" + s.id} className={"wrHorario" + (r ? " wrConRegla" : "")}>
									<div className="wrHorarioNombre"><b>{horarioAmPm(s.name, horarioBonito)}</b><span className="wrTenue">{s.courseName} · {s.periodName} · vence el {s.dueDay || "—"} · {s.enrolled} alumnos</span></div>
									<div className="wrHorarioAntes"><span className="wrTenue">Antes </span>{s.legacy.steps.length ? stepsText(s.legacy.steps, sym) : "—"}{s.differsFromLegacy ? <span className="wrOro"> · distinto</span> : null}</div>
									<div className="wrHorarioHoy"><span className="wrTenue">Hoy </span>{eff ? (eff.active ? "" : "Apagado · ") + stepsText(eff.steps, sym) + " (" + eff.scope + ")" : "—"}</div>
									<div className="wrHorarioAcc">
										<button type="button" className="wrLink" onClick={() => setEditing("s" + s.id)}>{r ? "Cambiar" : "Personalizar"}</button>
										{!r && s.legacy.valid && s.differsFromLegacy ? <button type="button" className="wrLink" onClick={() => fromLegacy(s)}>Usar lo del Horario</button> : null}
									</div>
								</div>
							);
						})}
						{!schedules.length ? <div className="wrTenue" style={{ padding: 8 }}>Sin horarios.</div> : null}
					</div>

					<h5 className="wrSeccion">Cambios</h5>
					{(data.history || []).length ? (data.history || []).map((h: any, i: number) => (
						<div key={"h" + i} className="wrCambio"><span className="wrTenue">{fechaHoraAmPm(h.at)} · {(h.by || "").trim() || "Sistema"}</span> · {h.action} {alcance(String(h.scopeKey || ""))}
							{h.after ? <> → {[h.after.active === null || h.after.active === undefined ? "" : h.after.active ? "encendido" : "apagado", h.after.steps ? stepsText(h.after.steps, sym) : "", h.after.validFrom ? "desde " + fecha(h.after.validFrom) : ""].filter(Boolean).join(" · ")}</> : null}</div>
					)) : <div className="wrTenue">Sin cambios todavía.</div>}
				</>
			)}
		</div>
	);
};

export default SurchargeRulesController;
